import { events as eventsSelectors } from '@eb/destination-utils';
import { formatUrl } from 'url-lib';
import { analyticsAction } from 'js-utils/analytics';

import { transformDestinationEventsData } from '../api/transformations/destinationEvents';
import { transformSponsorshipAssetsData } from '../api/transformations/sponsorshipAssets';
import { getSponsorshipAssets } from '../api/sponsorship';
import { getEventId } from '../selectors/event';
import { getUserId } from '../selectors/user';

const {
    selectors: { formatEventDataForDisplay },
} = eventsSelectors;

/* CONSTANTS */
export const MAX_PROMOTED_EVENTS = 4;
export const INITIALIZE_PROMOTED_EVENTS = 'initializePromotedEvents';
export const UTM_ASSET_TYPE_ORDER_CONF_PAGE = 'e2e-order-page';
export const EVENT_TO_EVENT_ORDER_CONF_PAGE_CODE = 'ebscccetoeconfpage';
export const SPONSOR_ASSET_EVENT_EXPANSION_PARAMS = {
    'expand.sponsor_asset': ['event', 'event.image', 'event.primary_venue'],
    asset_types: ['order_confirmation_page_event'],
};
export const SPONSORSHIP_ANALYTICS_ACTION = 'SponsorshipAction';

/* ACTIONS */
const _initializePromotedEvents = (data) => ({
    type: INITIALIZE_PROMOTED_EVENTS,
    payload: data,
});

/* HELPER FUNCTIONS */
export const _transformEventUrlWithAffiliateParams = (eventUrl, assetId) => {
    const params = {
        utm_source: 'e2e',
        utm_medium: 'web',
        utm_campaign: UTM_ASSET_TYPE_ORDER_CONF_PAGE,
        utm_content: 'textlink',
        utm_term: assetId,
    };

    const url = formatUrl(eventUrl, params);

    return url;
};

const _extractEventsFromAssets = (assets) =>
    assets.map((asset) => ({
        ...asset.event,
        url: _transformEventUrlWithAffiliateParams(asset.event.url, asset.id),
    }));

export const _transformEventsForDisplay = (events) =>
    formatEventDataForDisplay({
        events: transformDestinationEventsData(events).events,
        affCode: EVENT_TO_EVENT_ORDER_CONF_PAGE_CODE,
    });

/* ACTION DISPATCHERS */

// uses EB specific internal tracking as opposed to GA analytics
export const trackPromotedEventsImpression = (sponsorshipAssets) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const order = state.order;
    const guestId = state.app.guestId;
    const userId = getUserId(state);

    sponsorshipAssets.forEach((asset) => {
        const dimensions = {
            order_id: order.id,
            sponsee_event_id: order.eventId,
            user_id: userId,
            guest_id: guestId,
            action_type: 'assetImpression',
            asset_type: asset.assetType,
            image_url: asset.imageUrl,
            offer_asset_id: asset.id,
            offer_id: asset.offerId,
            organization_asset_id: asset.organizationAssetId,
            sponsor_event_id: asset.sponsoringEventId,
            sponsor_organization_id: asset.organizationId,
        };

        analyticsAction(SPONSORSHIP_ANALYTICS_ACTION, dimensions);
    });
};

export const getPromotedEvents = () => async (dispatch, getState) => {
    const state = getState();
    const eventId = getEventId(state);

    const {
        response: { assets },
    } = await getSponsorshipAssets(
        eventId,
        SPONSOR_ASSET_EVENT_EXPANSION_PARAMS,
    );

    if (assets.length) {
        // TODO EB-117941: instead of slicing the amount of events returned from get event assets,
        // replace with pagination of the get event assets call.
        const { assets: transformedAssets } = transformSponsorshipAssetsData(
            assets,
        );
        const events = _extractEventsFromAssets(assets).slice(
            0,
            MAX_PROMOTED_EVENTS,
        );
        const transformedEvents = _transformEventsForDisplay(events);

        // note: if other asset_types other than 'event' are introduced, will need to figure out collusion
        // in the store
        dispatch(trackPromotedEventsImpression(transformedAssets));

        return dispatch(
            _initializePromotedEvents({ promotedEvents: transformedEvents }),
        );
    }

    return null;
};
